import React, { createContext, useCallback, useContext, useRef, useMemo } from 'react'
import { FastAverageColor } from 'fast-average-color'
import { i18n } from '@fs/zion-locale'
import { useParams } from '@fs/zion-router'

import { BASE_KEEP_FONTS } from '../../helpers/keep/constants'
import { useKeep } from '../useKeep'
import useImageSettings from '../useImageSettings/useImageSettings'

const fastAverageColor = new FastAverageColor()

const ReadyContext = createContext()

export function ReadyProvider({ children }) {
  const {
    dynamicallySetPrintStrategy,
    elementRef,
    filename,
    fontElements,
    fonts,
    imageSize,
    pageOrientation,
    processing,
    setIsMultiPage,
    setIsReady,
    shouldCenter,
    totalPages,
  } = useKeep()
  const { setHasDarkBackground, IMAGE_HEIGHT, IMAGE_WIDTH } = useImageSettings()
  const backgroundReady = useRef(false)
  const portraitsReady = useRef(false)

  const { design } = useParams()

  const onIsReady = useCallback(() => {
    dynamicallySetPrintStrategy()
    filename.current = i18n.t(`design.name.${design}`, design)
    fontElements.current = [elementRef.current]
    fonts.current = [...BASE_KEEP_FONTS]
    imageSize.current = [IMAGE_WIDTH, IMAGE_HEIGHT]
    pageOrientation.current = IMAGE_HEIGHT > IMAGE_WIDTH ? 'portrait' : 'landscape'
    shouldCenter.current = processing?.fileType !== 'jpeg'
    totalPages.current = 1
    setIsMultiPage(false)
    setIsReady(true)
  }, [
    IMAGE_HEIGHT,
    IMAGE_WIDTH,
    design,
    dynamicallySetPrintStrategy,
    elementRef,
    filename,
    fontElements,
    fonts,
    imageSize,
    pageOrientation,
    processing,
    setIsMultiPage,
    setIsReady,
    shouldCenter,
    totalPages,
  ])

  const onBackgroundReady = useCallback(
    (event) => {
      const height = event.target.naturalHeight
      const width = event.target.naturalWidth

      fastAverageColor.getColorAsync(event.target.src, {
        insetInlineStart: (width / 4) * 3,
        top: (height / 4) * 3,
        width: width / 4,
        height: height / 4,
      })

      fastAverageColor
        .getColorAsync(event.target.src, {
          algorithm: 'sqrt',
        })
        .then((color) => {
          setHasDarkBackground(color.isDark)
        })

      backgroundReady.current = true
      onIsReady()
    },
    [onIsReady, setHasDarkBackground]
  )

  const onPortraitsReady = useCallback(() => {
    portraitsReady.current = true
    if (backgroundReady.current) {
      onIsReady()
    }
  }, [onIsReady])

  const memoizedValue = useMemo(
    () => ({
      onBackgroundReady,
      onPortraitsReady,
    }),
    [onBackgroundReady, onPortraitsReady]
  )

  return <ReadyContext.Provider value={memoizedValue}>{children}</ReadyContext.Provider>
}

export function useReady() {
  return useContext(ReadyContext)
}
